/*
 * Star rows: live REVIEWS.io numbers after consent (D-30).
 *
 * Does for the theme's star rows what the REVIEWS.io rating-snippet embed's
 * dist.js did, so the embed can be switched off. Rows are every
 * .ruk_rating_snippet[data-sku] (cards, article strips); the PDP button has no
 * data-sku and keeps its server values. Rows arrive server-rendered from the
 * product metafields; this script re-renders them from the embed's endpoint:
 *   - one request per pass: api.reviews.io/product/rating-batch?sku=<the rows'
 *     data-sku lists in DOM order, deduped, joined by ";">&store=<store>&lang=
 *     <reviewsIoLang>[&enableSyndication=true] - dist.js's query, from the
 *     globals of the REVIEWS.io core embed; POSTed when the query passes 8000
 *     characters, as dist.js does; no cookies, origin-only referrer;
 *   - value, as dist.js getRatingSnippetValues: every returned item whose sku,
 *     or an entry of its `lookup`, equals one of the row's SKUs (trimmed,
 *     lower-cased), each item once; rating = sum(average_rating * num_ratings)
 *     / sum(num_ratings), count = sum(num_ratings);
 *   - stars, as dist.js renderSnippet: the rating rounded to a quarter (an
 *     unparsable rating gives five empty stars).
 * A row no item matches keeps its server values; a failure leaves every row as
 * rendered, silently.
 *
 * Consent: the embed only runs once Pandectes' blocker allows the category it
 * files reviews.io under - Marketing (4) on sheko.com, Analytics (2) on
 * sheko.ch (measured 2026-09-26). The script tag carries that category as
 * data-consent-category (snippets/star-rating-sync.liquid); without it, or
 * with any value other than 1, 2 or 4, nothing is ever fetched. The blocker's
 * state is read the way the Pandectes rules module sets it:
 *   - the latest dataLayer event Pandectes_Consent_Update, whose
 *     pandectes_categories C0001/C0002/C0003 are categories 1/2/4 (pushed
 *     only when the store enables Pandectes' custom-event integration - on
 *     sheko.com, off on sheko.ch);
 *   - otherwise the latest PandectesConsentUpdateLegacy window event, or the
 *     stored _pandectes_gdpr consent (preferences = the blocked categories).
 * A later event is picked up through dataLayer.push (as
 * snippets/datalayer.liquid does) or the window event. Rows added later
 * (recommendations, filtered grids, the calculator's card) are picked up by a
 * MutationObserver, as the embed's listenForChanges did.
 */
(() => {
  if (window.starRatingSync) return;
  window.starRatingSync = 1;

  const EVENT = 'Pandectes_Consent_Update';
  const API = 'https://api.reviews.io/product/rating-batch';
  // One of the Pandectes bitmask categories (1 Preferences, 2 Analytics,
  // 4 Marketing). A missing or other value means no fetch, ever.
  const category = Number(document.currentScript && document.currentScript.dataset.consentCategory);
  const gated = category === 1 || category === 2 || category === 4;
  const seen = new WeakSet();
  const lower = (value) => String(value).trim().toLowerCase();
  let loaded;
  let legacy;
  let observer;
  let timer;
  let first;

  const stored = () => {
    try {
      const { duration, lastReset } = window.pandectesRulesSettings.storage;
      const cookie = document.cookie.split('; ').find((entry) => entry.startsWith('_pandectes_gdpr='));
      const consent = JSON.parse(cookie ? atob(cookie.slice(16)) : localStorage.getItem('_pandectes_gdpr'));
      const age = Math.floor(Date.now() / 1000) - consent.timestamp;
      return duration > 0 && (consent.timestamp < lastReset || age > 86400 * duration) ? null : consent.preferences;
    } catch (error) {
      return null;
    }
  };

  const consented = () => {
    if (!gated) return false;
    let allow;
    for (const entry of window.dataLayer || []) {
      if (entry && entry.event === EVENT) allow = (entry.pandectes_categories || {})[`C000${category === 4 ? 3 : category}`] === 'allow';
    }
    if (allow !== undefined) return allow;
    const blocked = legacy !== undefined ? legacy : stored();
    return blocked != null && !(blocked & category);
  };

  const paint = (row, items) => {
    const skus = row.dataset.sku.split(';').filter(Boolean).map(lower);
    const used = [];
    let sum = 0;
    let count = 0;
    for (const key in items) {
      const item = items[key];
      if (item && item.sku != null && !used.includes(item.sku) && skus.some((sku) => sku === lower(item.sku) || (item.lookup && lower(item.lookup).split(';').includes(sku)))) {
        used.push(item.sku);
        sum += parseFloat(item.average_rating) * item.num_ratings;
        count += parseFloat(item.num_ratings);
      }
    }
    if (!(count > 0)) return;
    const average = sum / count;
    const rating = isFinite(average) ? Math.round(4 * average) / 4 : 0;
    let html = '';
    for (let star = 1; star <= 5; star++) {
      html += `<i aria-hidden="true" class="ruk-icon-percentage-star--${Math.max(0, Math.min(1, rating - star + 1)) * 100}"></i>`;
    }
    row.innerHTML = `${html}<span class="ruk-rating-snippet-count">(${count})</span>`;
  };

  const sync = () => {
    const store = window.__REVIEWSIO_RATING_SNIPPET_STORE_OVERRIDE__ || window.reviewsIoStore;
    if (!loaded || !store || !window.fetch || !consented()) return;
    const rows = [...document.querySelectorAll('.ruk_rating_snippet[data-sku]')].filter((row) => !seen.has(row));
    rows.forEach((row) => seen.add(row));
    const list = encodeURIComponent([...new Set(rows.flatMap((row) => row.dataset.sku.split(';')))].join(';'));
    if (list) {
      const query = `sku=${list}&store=${store}&lang=${window.reviewsIoLang || 'en'}${window.reviewsIoEnableSyndication ? '&enableSyndication=true' : ''}`;
      const post = query.length > 8000;
      const init = { credentials: 'omit', referrerPolicy: 'origin' };
      fetch(post ? API : `${API}?${query}`, post ? { ...init, method: 'POST', body: new URLSearchParams(query) } : init)
        .then((response) => (response.ok ? response.json() : Promise.reject()))
        .then((items) => rows.forEach((row) => paint(row, items)))
        .catch(() => {});
    }
    if (observer) return;
    // Trailing 500 ms, but never more than 2 s after the first change of a burst.
    observer = new MutationObserver(() => {
      if (!timer) first = Date.now();
      clearTimeout(timer);
      timer = setTimeout(() => {
        timer = 0;
        sync();
      }, Math.max(0, Math.min(500, first + 2000 - Date.now())));
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  const dataLayer = (window.dataLayer = window.dataLayer || []);
  const push = dataLayer.push;
  dataLayer.push = function (...entries) {
    const result = push.apply(this, entries);
    if (entries.some((entry) => entry && entry.event === EVENT)) setTimeout(sync);
    return result;
  };
  addEventListener('PandectesConsentUpdateLegacy', (event) => {
    if (event.detail && event.detail.preferences != null) legacy = event.detail.preferences;
    setTimeout(sync);
  });

  const start = () => {
    loaded = 1;
    if (window.requestIdleCallback) requestIdleCallback(sync);
    else setTimeout(sync);
  };
  if (document.readyState === 'complete') start();
  else addEventListener('load', start, { once: true });
})();
